import { boot } from "quasar/wrappers";
import { Notify } from "quasar";
import axios from "axios";
import pako from 'pako';
import { useGlobalTheme } from "../composables/useGlobalTheme";

var qbankConfig = require('../config/qbank.js');
import CryptoJS from "crypto-js";
import { Sentry } from "./sentry";



// Shuffled key segments
const AES_KEY_PARTS = ["2kuz", "riSQ", "dgS6", "1RLQ", "KqU0", "tioH", "Tdy4", "JIi7"];
const AES_IV_PARTS = ["BXSog", "XD93", "aV8n", "I2m"];

// Index to reconstruct the original order
const AES_KEY_INDEX = [5, 2, 1, 6, 4, 7, 0, 3];
const AES_IV_INDEX = [2, 1, 0, 3];

// Reconstruct dynamically
const AES_KEY = AES_KEY_INDEX.map(i => AES_KEY_PARTS[i]).join("");
const AES_IV = AES_IV_INDEX.map(i => AES_IV_PARTS[i]).join("");




// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
const api = axios.create({ baseURL: "https://api.example.com" });

export default boot(({ app }) => {
  // for use inside Vue files (Options API) through this.$axios and this.$api

  app.config.globalProperties.$axios = axios;
  // ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
  //       so you won't necessarily have to import axios in each vue file

  app.config.globalProperties.$api = api;
  // ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
  //       so you can easily perform requests against your app's API



  // Helper function to get CSRF token from cookie
  const getCsrfToken = () => {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };

  // Make getCsrfToken available globally for fetch() calls
  app.config.globalProperties.$getCsrfToken = getCsrfToken;
  window.getCsrfToken = getCsrfToken;

  // Add an interceptor to attach CSRF token and qbank_source to all requests
  axios.interceptors.request.use((config) => {
      // Add CSRF token header for Laravel validation
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-XSRF-TOKEN'] = csrfToken;
      }

      // Check if Content-Type is application/json - preserve JSON format
      const contentType = config.headers['Content-Type'] || config.headers['content-type'];
      const isJsonRequest = contentType && contentType.includes('application/json');

      // Browser UTC offset (minutes east of UTC; UTC+3 => +180). Sent on every request
      // like qbank_source so the FSRS "study day" / due / forecast compute in the user's
      // LOCAL timezone (Anki day-model), not the server's UTC day. Safari-12 safe.
      const tzOff = -(new Date().getTimezoneOffset());

      // Skip FormData transformation for JSON requests or requests with skipFormData flag
      if (isJsonRequest || config.skipFormData) {
        // For JSON requests, add qbank_source to the data object directly
        if (config.data && typeof config.data === 'object' && !config.data.qbank_source) {
          config.data.qbank_source = qbankConfig.getConfig().qbank_source;
        }
        if (config.data && typeof config.data === 'object' && config.data.tz_offset_minutes === undefined) {
          config.data.tz_offset_minutes = tzOff;
        }
        // Ensure Content-Type is set for JSON requests
        if (!contentType) {
          config.headers['Content-Type'] = 'application/json';
        }
      } else if (config.data instanceof FormData) {
        // If the request data is already a FormData instance, just append the new property
        config.data.append("qbank_source", qbankConfig.getConfig().qbank_source);
        if (!config.data.has("tz_offset_minutes")) {
          config.data.append("tz_offset_minutes", tzOff);
        }
      } else {
        // If the request data is not a FormData instance, create a new FormData instance and append the new property
        const formData = new FormData();
        formData.append("qbank_source", qbankConfig.getConfig().qbank_source);
        for (const key in config.data) {
          formData.append(key, config.data[key]);
        }
        if (!formData.has("tz_offset_minutes")) {
          formData.append("tz_offset_minutes", tzOff);
        }
        config.data = formData;
      }
      return config;
    });


    // Track if session expired dialog is already showing to prevent duplicates
    let sessionExpiredDialogShowing = false;

    axios.interceptors.response.use(
      (response) => {
        if (response.data && response.data.encrypted) {
          try {
            const trimmedEncrypted = response.data.encrypted.slice(0, -6);
            // 1. Base64 decode the encrypted payload
            const decodedData = atob(trimmedEncrypted);

            // 2. Convert binary string to CryptoJS WordArray (LATIN1)
            const ciphertext = CryptoJS.enc.Latin1.parse(decodedData);

            // 3. Decrypt with AES-CBC
            const bytes = CryptoJS.AES.decrypt(
              { ciphertext: ciphertext }, // Pass as ciphertext property
              CryptoJS.enc.Utf8.parse(AES_KEY),
              {
                iv: CryptoJS.enc.Utf8.parse(AES_IV),
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7
              }
            );

            // 4. Convert decrypted WordArray to Uint8Array
            const compressedBytes = new Uint8Array(
              bytes.toString(CryptoJS.enc.Latin1)
                .split('')
                .map(c => c.charCodeAt(0))
            );

            // 5. Decompress with pako
            const jsonString = pako.inflate(compressedBytes, { to: 'string' });


            response.data = JSON.parse(jsonString);
          } catch (error) {
            console.error("Decryption error:", error);
            // Capture decryption errors in Sentry
            // Note: Avoiding optional chaining (?.) for Safari 12/iOS 12 compatibility
            if (Sentry && Sentry.captureException) {
              Sentry.captureException(error, {
                tags: { errorType: 'decryption' },
                extra: { url: response.config && response.config.url }
              });
            }
            return Promise.reject(error);
          }
        }
        return response;
      },
      (error) => {
        // Get response status and data safely (Safari 12 compatible - no optional chaining)
        var responseStatus = error.response && error.response.status;
        var responseData = error.response && error.response.data;
        var errorCode = responseData && responseData.error_code;

        // Handle 401 with error_code='session_expired' - show dialog and redirect
        // Only this specific case is excluded from Sentry (expected behavior)
        if (responseStatus === 401 && errorCode === 'session_expired') {
          console.warn('Session expired - redirecting to login');

          // Only show dialog once (prevent multiple dialogs from concurrent requests)
          if (!sessionExpiredDialogShowing) {
            sessionExpiredDialogShowing = true;

            // Get message from response or use default
            var message = (responseData && responseData.message) || 'انتهت صلاحية الجلسة';
            var messageDetail = (responseData && responseData.message_detail) || 'يرجى تسجيل الدخول مرة أخرى للمتابعة';

            // Theme-matched dialog colors — the card is body-appended with
            // inline styles, so the values are branched at build time from the
            // global theme singleton (Safari 12: no optional chaining). Light
            // keeps the exact original values.
            var seCardBg = '#fff';
            var seTitle = '#333';
            var seBody = '#666';
            var seAccent = '#1976d2';
            var seGt = useGlobalTheme();
            var seTheme = (seGt && seGt.colorTheme && typeof seGt.colorTheme.value === 'string') ? seGt.colorTheme.value : 'theme-light';
            if (seTheme === 'theme-dark') {
              seCardBg = '#222222'; seTitle = '#e0e0e0'; seBody = '#9aa4b2'; seAccent = '#2196f3';
            } else if (seTheme === 'theme-sepia') {
              seCardBg = '#FBF0DA'; seTitle = '#5f4938'; seBody = '#8a7355'; seAccent = '#8a5a2b';
            }

            // Create and show session expired dialog
            var dialogOverlay = document.createElement('div');
            dialogOverlay.id = 'session-expired-dialog';
            dialogOverlay.innerHTML = '\
              <div style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.7);z-index:99999;display:flex;align-items:center;justify-content:center;">\
                <div style="background:' + seCardBg + ';border-radius:12px;padding:32px;max-width:400px;width:90%;text-align:center;box-shadow:0 4px 20px rgba(0,0,0,0.3);">\
                  <div style="font-size:48px;margin-bottom:16px;">🔒</div>\
                  <h2 class="Tajawal-Regular" style="font-size:24px;margin:0 0 12px 0;color:' + seTitle + ';">' + message + '</h2>\
                  <p class="Tajawal-Regular" style="font-size:16px;color:' + seBody + ';margin:0 0 24px 0;">' + messageDetail + '</p>\
                  <div style="display:flex;gap:10px;">\
                    <button id="session-expired-reload-btn" class="Tajawal-Regular" style="background:' + seCardBg + ';color:' + seAccent + ';border:2px solid ' + seAccent + ';border-radius:8px;padding:12px 16px;font-size:16px;cursor:pointer;flex:1;">اعادة تحميل الصفحة</button>\
                    <button id="session-expired-login-btn" class="Tajawal-Regular" style="background:' + seAccent + ';color:#fff;border:none;border-radius:8px;padding:12px 16px;font-size:16px;cursor:pointer;flex:1;">تسجيل الدخول</button>\
                  </div>\
                </div>\
              </div>';

            document.body.appendChild(dialogOverlay);

            // Add click handlers
            document.getElementById('session-expired-login-btn').addEventListener('click', function() {
              // Redirect to main site login
              window.location.href = 'https://yourmedpass.com/login?redirect=' + encodeURIComponent(window.location.href);
            });
            document.getElementById('session-expired-reload-btn').addEventListener('click', function() {
              window.location.reload();
            });
          }

          // Return rejected promise but don't send to Sentry (this is expected behavior)
          return Promise.reject(error);
        }

        var isCanceled = error.code === 'ERR_CANCELED';
        var configUrl = error.config && error.config.url ? error.config.url : '';
        var isAnalytics = configUrl.indexOf('analytics') !== -1 || configUrl.indexOf('track') !== -1;
        var isSilent = error.config && error.config.silentError;

        // For other errors, capture in Sentry — EXCEPT:
        //  - silentError requests with a 4xx response: the CALLER declared it
        //    owns this error and handles it gracefully (e.g. deleting an
        //    already-deleted quiz → 404 → its own toast). Capturing those
        //    flooded the catch-all AxiosError group with handled, expected
        //    flows (Sentry QBANK-FRONTEND-AV: 800+ events). 5xx and
        //    no-response failures on silentError requests STAY captured —
        //    those are server/infra signals, not caller-handled flows.
        //  - canceled requests: the user navigated away mid-request — not an
        //    error (Sentry QBANK-FRONTEND-1SB and siblings).
        // Note: Avoiding optional chaining (?.) for Safari 12/iOS 12 compatibility
        var isSilentHandled = isSilent && responseStatus && responseStatus < 500;
        if (Sentry && Sentry.captureException && !isSilentHandled && !isCanceled) {
          Sentry.captureException(error, {
            tags: { errorType: 'api' },
            extra: {
              url: error.config && error.config.url,
              method: error.config && error.config.method,
              status: responseStatus
            }
          });
        }

        // Show user-friendly toast for common error types
        // Skip for canceled requests (user navigated away) and analytics/non-critical calls

        if (!isCanceled && !isAnalytics && !isSilent) {
          var userMessage = '';

          if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') {
            userMessage = 'تعذر الاتصال بالخادم. تحقق من اتصالك بالإنترنت وحاول مرة أخرى.';
          } else if (responseStatus === 400) {
            var serverMsg = responseData && responseData.message;
            userMessage = serverMsg || 'حدث خطأ في الطلب. يرجى المحاولة مرة أخرى.';
          } else if (responseStatus === 403) {
            userMessage = 'ليس لديك صلاحية للقيام بهذا الإجراء.';
          } else if (responseStatus === 500) {
            userMessage = 'حدث خطأ في الخادم. يرجى المحاولة مرة أخرى لاحقاً.';
          }

          if (userMessage) {
            Notify.create({
              type: 'negative',
              message: userMessage,
              position: 'top',
              timeout: 5000,
              actions: [{ icon: 'close', color: 'white' }]
            });
          }
        }

        return Promise.reject(error);
      }
    );

/* before deep seek gzcompression
   axios.interceptors.response.use(
      (response) => {
        if (response.data && response.data.encrypted) {
          try {
            let ciphertext;

            // Attempt to trim and decode double Base64
            try {
              const trimmedEncrypted = response.data.encrypted.slice(0, -6);
              ciphertext = CryptoJS.enc.Base64.parse(trimmedEncrypted).toString(CryptoJS.enc.Utf8);
            } catch (innerError) {
              // Fallback: Use raw data if trimming/decoding fails
              ciphertext = response.data.encrypted.slice(0, -6);;
            }

            // Decrypt the ciphertext (either decoded or raw)
            const bytes = CryptoJS.AES.decrypt(
              ciphertext, // Could be Base64 or raw data
              CryptoJS.enc.Utf8.parse(AES_KEY),
              {
                iv: CryptoJS.enc.Utf8.parse(AES_IV),
                mode: CryptoJS.mode.CBC,
                padding: CryptoJS.pad.Pkcs7,
              }
            );

            const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            response.data = decryptedData;
          } catch (error) {
            console.error("Decryption error:", error);
            return Promise.reject(error);
          }
        }
        return response;
      },
      (error) => Promise.reject(error)
    );
*/


});




export { api };
